import { AllocationRuleDto, CarrierType, ContainerHeight, ContainerType } from '@yard/app/api'
import { ContainerLength } from '@yard/components/AutoCompletes/ContainerLengthAutoComplete/ContainerLengthAutoComplete'
import { OccupancyDataDto, OcuppancyWithFilterData } from '@yard/stores/OccupancyStore'
import { generateSlotKey } from '../pages/YardSetup/helpers/yard-view.utils'
import { ColorScheme, YardFilters } from '../pages/YardSetup/stores/yard.view-store'
import {
  OTHER_REST_COLOR,
  OTHER_REST_VALUE,
  paletteColorFor,
} from './color-scheme-palette'
const HOVER_SHADOW_THICKNESS = 5

// Occupancy fill colors
export const OCCUPANCY_EMPTY = '#FFFFFF'
export const OCCUPANCY_PARTIAL = '#B0BEC5'
export const OCCUPANCY_FULL = '#546E7A'

// Filter overlay
export const FILTERED_OVERLAY = 'rgb(0 255 10 / 25%)'

// Slot borders
export const BORDER_DEFAULT = '#CFD8DC'
export const BORDER_RULE = '#1565C0'
export const BORDER_RULE_HOVERED = '#22ca12'
export const HIGHLIGHT_BORDER = '#1976D2'

// Hover shadow
export const HOVER_SHADOW = '#22ca12'

// Light text (used over dark backgrounds — full slots, dark palette colours).
// The matching dark text comes from the theme's primary text colour.
export const TEXT_ON_FULL = '#FFFFFF'

// Pick light or dark text based on the background's relative luminance.
// Threshold 0.179 is the WCAG cross-over where contrast with pure black and
// pure white is equal — accepts hex strings (#RGB or #RRGGBB).
export const pickContrastText = (
  bgHex: string,
  lightText: string,
  darkText: string,
): string => {
  const h = bgHex.replace('#', '')
  const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h
  const r = parseInt(full.slice(0, 2), 16)
  const g = parseInt(full.slice(2, 4), 16)
  const b = parseInt(full.slice(4, 6), 16)
  const channel = (c: number) => {
    const v = c / 255
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4)
  }
  const lum = 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b)
  return lum < 0.179 ? lightText : darkText
}

// Blocked pattern colors (used by `blockedBackgroundPattern`)
export const BLOCKED_PATTERN_EMPTY = '#CFD8DC'
export const BLOCKED_PATTERN_PARTIAL = '#607D8B'
export const BLOCKED_PATTERN_FULL = '#263238'

// Border widths
export const BORDER_WIDTH_DEFAULT = '1px'
export const BORDER_WIDTH_HIGHLIGHTED = '4px'

export interface SlotRuleData {
  isMatch: boolean
  isHoveredMatch: boolean
  edges: { top: boolean; right: boolean; bottom: boolean; left: boolean }
  hoveredEdges: { top: boolean; right: boolean; bottom: boolean; left: boolean }
}
export const mergeSlotRuleDataForSpan = (
  slotRuleMap: Record<string, SlotRuleData>,
  bayIdx: number,
  rowIdx: number,
  span: number,
): SlotRuleData | undefined => {
  const firstKey = generateSlotKey(bayIdx, rowIdx)
  const first = slotRuleMap[firstKey]
  if (span <= 1 || !first) return first

  const lastKey = generateSlotKey(bayIdx + span - 1, rowIdx)
  const last = slotRuleMap[lastKey]

  return {
    isMatch: first.isMatch,
    isHoveredMatch: first.isHoveredMatch,
    edges: {
      top: first.edges.top || (last?.edges.top ?? false),
      bottom: first.edges.bottom || (last?.edges.bottom ?? false),
      left: first.edges.left,
      right: last?.edges.right ?? first.edges.right,
    },
    hoveredEdges: {
      top: first.hoveredEdges.top || (last?.hoveredEdges.top ?? false),
      bottom: first.hoveredEdges.bottom || (last?.hoveredEdges.bottom ?? false),
      left: first.hoveredEdges.left,
      right: last?.hoveredEdges.right ?? first.hoveredEdges.right,
    },
  }
}

export interface SlotHighlightStyle {
  backgroundColor: string
  filterOverlay: string | null
  textColor: string
  blockedPatternColor: string
  boxShadow: string
  borderWidth: string
  borderStyle: string
  borderColor: string
}

export const precomputeSlotRuleMap = (
  blockId: string,
  maxBays: number,
  maxRows: number,
  allocationRules: AllocationRuleDto[],
  selectedAllocationRuleIndex: number | undefined,
): Record<string, SlotRuleData> => {
  const map: Record<string, SlotRuleData> = {}

  const selectedRule =
    selectedAllocationRuleIndex !== undefined
      ? allocationRules[selectedAllocationRuleIndex]
      : undefined

  // First pass: mark which slots are matched and hovered
  for (const rule of allocationRules) {
    const isHovered = rule === selectedRule

    for (const dest of rule.destinations) {
      if (dest.blockId !== blockId) continue

      const minBay = dest.minBay ?? 1
      const maxBay = dest.maxBay ?? maxBays
      const minRow = dest.minRow ?? 1
      const maxRow = dest.maxRow ?? maxRows

      for (let bay = minBay; bay <= Math.min(maxBay, maxBays); bay++) {
        for (let row = minRow; row <= Math.min(maxRow, maxRows); row++) {
          const key = generateSlotKey(bay, row)

          if (!map[key]) {
            map[key] = {
              isMatch: false,
              isHoveredMatch: false,
              edges: { top: false, right: false, bottom: false, left: false },
              hoveredEdges: { top: false, right: false, bottom: false, left: false },
            }
          }

          map[key].isMatch = true
          if (isHovered) map[key].isHoveredMatch = true
        }
      }
    }
  }

  // Second pass: detect edges by checking neighbors
  for (let bay = 1; bay <= maxBays; bay++) {
    for (let row = 1; row <= maxRows; row++) {
      const key = generateSlotKey(bay, row)
      const slot = map[key]
      if (!slot?.isMatch) continue

      if (bay === 1 || !map[generateSlotKey(bay - 1, row)]?.isMatch) slot.edges.left = true
      if (bay === maxBays || !map[generateSlotKey(bay + 1, row)]?.isMatch) slot.edges.right = true
      if (row === 1 || !map[generateSlotKey(bay, row - 1)]?.isMatch) slot.edges.top = true
      if (row === maxRows || !map[generateSlotKey(bay, row + 1)]?.isMatch) slot.edges.bottom = true

      if (slot.isHoveredMatch) {
        if (bay === 1 || !map[generateSlotKey(bay - 1, row)]?.isHoveredMatch)
          slot.hoveredEdges.left = true
        if (bay === maxBays || !map[generateSlotKey(bay + 1, row)]?.isHoveredMatch)
          slot.hoveredEdges.right = true
        if (row === 1 || !map[generateSlotKey(bay, row - 1)]?.isHoveredMatch)
          slot.hoveredEdges.top = true
        if (row === maxRows || !map[generateSlotKey(bay, row + 1)]?.isHoveredMatch)
          slot.hoveredEdges.bottom = true
      }
    }
  }

  return map
}

export const computeSlotStyle = (
  showAllocationRulesPreview: boolean,
  isHighlighted: boolean,
  isFiltered: boolean,
  slotRuleData: SlotRuleData | undefined,
  textDefault: string,
  occupancyCount: number,
  effectiveMaxTiers: number,
  schemeBackgroundColor: string | null,
): SlotHighlightStyle => {
  const borderStyle = 'solid'
  const isFull = effectiveMaxTiers > 0 && occupancyCount >= effectiveMaxTiers
  const isPartial = occupancyCount > 0 && !isFull
  const defaultBackground = isFull ? OCCUPANCY_FULL : isPartial ? OCCUPANCY_PARTIAL : OCCUPANCY_EMPTY
  // When a colour-by scheme is active, the caller passes a non-null colour for
  // every slot: the palette colour for matched slots, OCCUPANCY_EMPTY for
  // unmatched/no-value/unoccupied. When no scheme is active the caller passes
  // null and the occupancy gray scale (full/partial/empty) is used.
  const backgroundColor = schemeBackgroundColor ?? defaultBackground
  const filterOverlay = isFiltered ? FILTERED_OVERLAY : null
  // Pick text colour from the actual background luminance so labels stay
  // legible whether the slot is rendering an occupancy gray scale or a
  // saturated palette colour from a colour-by scheme.
  const textColor = pickContrastText(backgroundColor, TEXT_ON_FULL, textDefault)
  const blockedPatternColor = isFull
    ? BLOCKED_PATTERN_FULL
    : isPartial
      ? BLOCKED_PATTERN_PARTIAL
      : BLOCKED_PATTERN_EMPTY

  if (isHighlighted) {
    return {
      backgroundColor,
      filterOverlay,
      textColor,
      blockedPatternColor,
      boxShadow: 'none',
      borderWidth: BORDER_WIDTH_HIGHLIGHTED,
      borderStyle,
      borderColor: HIGHLIGHT_BORDER,
    }
  }

  if (!showAllocationRulesPreview || !slotRuleData?.isMatch) {
    return {
      backgroundColor,
      filterOverlay,
      textColor,
      blockedPatternColor,
      boxShadow: 'none',
      borderWidth: BORDER_WIDTH_DEFAULT,
      borderStyle,
      borderColor: BORDER_DEFAULT,
    }
  }

  const { isHoveredMatch, edges, hoveredEdges } = slotRuleData

  const edgeColor = isHoveredMatch ? BORDER_RULE_HOVERED : BORDER_RULE
  const borderTop = edges.top ? edgeColor : BORDER_DEFAULT
  const borderRight = edges.right ? edgeColor : BORDER_DEFAULT
  const borderBottom = edges.bottom ? edgeColor : BORDER_DEFAULT
  const borderLeft = edges.left ? edgeColor : BORDER_DEFAULT
  const borderColor = `${borderTop} ${borderRight} ${borderBottom} ${borderLeft}`

  const borderTopWidth = edges.top ? BORDER_WIDTH_HIGHLIGHTED : BORDER_WIDTH_DEFAULT
  const borderRightWidth = edges.right ? BORDER_WIDTH_HIGHLIGHTED : BORDER_WIDTH_DEFAULT
  const borderBottomWidth = edges.bottom ? BORDER_WIDTH_HIGHLIGHTED : BORDER_WIDTH_DEFAULT
  const borderLeftWidth = edges.left ? BORDER_WIDTH_HIGHLIGHTED : BORDER_WIDTH_DEFAULT
  const borderWidth = `${borderTopWidth} ${borderRightWidth} ${borderBottomWidth} ${borderLeftWidth}`

  let boxShadow = 'none'
  if (isHoveredMatch) {
    const shadows: string[] = []
    const t = HOVER_SHADOW_THICKNESS
    if (hoveredEdges.left) shadows.push(`inset ${t}px 0 0 0 ${HOVER_SHADOW}`)
    if (hoveredEdges.right) shadows.push(`inset -${t}px 0 0 0 ${HOVER_SHADOW}`)
    if (hoveredEdges.top) shadows.push(`inset 0 ${t}px 0 0 ${HOVER_SHADOW}`)
    if (hoveredEdges.bottom) shadows.push(`inset 0 -${t}px 0 0 ${HOVER_SHADOW}`)
    if (shadows.length) boxShadow = shadows.join(', ')
  }

  return {
    backgroundColor,
    filterOverlay,
    textColor,
    blockedPatternColor,
    boxShadow,
    borderWidth,
    borderStyle,
    borderColor,
  }
}

export const blockedBackgroundPattern = (color: string) =>
  `linear-gradient(to top right, transparent calc(50% - 1px), ${color} calc(50% - 1px), ${color} calc(50% + 1px), transparent calc(50% + 1px)), linear-gradient(to bottom right, transparent calc(50% - 1px), ${color} calc(50% - 1px), ${color} calc(50% + 1px), transparent calc(50% + 1px))`

export const getSlotSpan = ({
  bayIdx,
  rowIdx,
  maxBays,
  occupancies,
}: {
  bayIdx: number
  rowIdx: number
  maxBays: number
  occupancies: Record<string, OccupancyDataDto[]>
}): number => {
  const key = generateSlotKey(bayIdx, rowIdx)
  const slotOccupancies = occupancies[key] || []
  let span = 1

  if (slotOccupancies.length > 0 && bayIdx + 1 <= maxBays) {
    const nextKey = generateSlotKey(bayIdx + 1, rowIdx)
    const nextOccupancies = occupancies[nextKey] || []
    if (
      slotOccupancies.length === nextOccupancies.length &&
      slotOccupancies.every((occ, idx) => occ.journeyId === nextOccupancies[idx].journeyId)
    ) {
      span = 2
    }
  }

  return span
}

export const areaMatchesAllocationRule = (
  blockId: string,
  rules: AllocationRuleDto[],
  currentBayIndex?: number,
  currentRowIndex?: number,
) => {
  if (!rules || rules.length === 0) return false

  return rules.some(rule =>
    rule.destinations.some(
      dest =>
        dest.blockId === blockId &&
        (dest.minBay === undefined ||
          (currentBayIndex !== undefined && dest.minBay <= currentBayIndex)) &&
        (dest.maxBay === undefined ||
          (currentBayIndex !== undefined && dest.maxBay >= currentBayIndex)) &&
        (dest.minRow === undefined ||
          (currentRowIndex !== undefined && dest.minRow <= currentRowIndex)) &&
        (dest.maxRow === undefined ||
          (currentRowIndex !== undefined && dest.maxRow >= currentRowIndex)),
    ),
  )
}

export const isOccupancyMatchingFilters = (
  occupancy: OcuppancyWithFilterData,
  filters: YardFilters,
): boolean => {
  const hasAnyFilter = Object.values(filters).some(
    filterValue =>
      filterValue !== undefined &&
      filterValue !== null &&
      !(Array.isArray(filterValue) && filterValue.length === 0) &&
      !(typeof filterValue === 'string' && filterValue.trim() === ''),
  )

  if (!hasAnyFilter) return false

  const checks: Array<() => boolean> = [
    // Container Number — partial match
    () =>
      filters.containerNumber === undefined ||
      filters.containerNumber.trim() === '' ||
      (!!occupancy.containerNumber &&
        occupancy.containerNumber
          .toLowerCase()
          .indexOf(filters.containerNumber.trim().toLowerCase()) !== -1),

    // Operator ID
    () =>
      filters.containerOperatorId === undefined ||
      occupancy.operatorId === filters.containerOperatorId,

    // Port of Loading
    () => filters.portOfLoading === undefined || occupancy.portOfLoading === filters.portOfLoading,

    // Port of Discharge
    () =>
      filters.portOfDischarge === undefined ||
      occupancy.portOfDischarge === filters.portOfDischarge,

    // Container Height — array includes
    () =>
      filters.containerHeight === undefined ||
      filters.containerHeight.length === 0 ||
      (!!occupancy.containerHeight &&
        filters.containerHeight.includes(occupancy.containerHeight as ContainerHeight)),

    // Container Length — array includes
    () =>
      filters.containerLength === undefined ||
      filters.containerLength.length === 0 ||
      (!!occupancy.length && filters.containerLength.includes(occupancy.length as ContainerLength)),

    // Container Type — array includes
    () =>
      filters.containerType === undefined ||
      filters.containerType.length === 0 ||
      (!!occupancy.containerType &&
        filters.containerType.includes(occupancy.containerType as ContainerType)),

    // Inbound Carrier Type — array includes
    () =>
      filters.inboundCarrierType === undefined ||
      (!!occupancy.inboundCarrierType &&
        filters.inboundCarrierType.includes(occupancy.inboundCarrierType as CarrierType)),

    // Outbound Carrier Type — array includes
    () =>
      filters.outboundCarrierType === undefined ||
      (!!occupancy.outboundCarrierType &&
        filters.outboundCarrierType.includes(occupancy.outboundCarrierType as CarrierType)),

    // Inbound Carrier Visit
    () =>
      filters.inboundCarrierVisitId === undefined ||
      occupancy.inboundCarrierVisitId === filters.inboundCarrierVisitId,

    // Outbound Carrier Visit
    () =>
      filters.outboundCarrierVisitId === undefined ||
      occupancy.outboundCarrierVisitId === filters.outboundCarrierVisitId,

    // isEmpty — dual inbound/outbound check
    () => {
      if (filters.isEmpty === undefined) return true
      const inboundMatch =
        occupancy.inboundIsEmpty != null && occupancy.inboundIsEmpty === filters.isEmpty
      const outboundMatch =
        occupancy.outboundIsEmpty != null && occupancy.outboundIsEmpty === filters.isEmpty
      return inboundMatch || outboundMatch
    },

    // isDangerous
    () =>
      filters.isDangerous === undefined ||
      (occupancy.inboundIsDangerous != null &&
        !!occupancy.inboundIsDangerous === filters.isDangerous),

    // isReefer
    () =>
      filters.isReefer === undefined ||
      (occupancy.isReefer != null && !!occupancy.isReefer === filters.isReefer),

    // isOOG
    () =>
      filters.isOOG === undefined ||
      (occupancy.inboundIsOOG != null && !!occupancy.inboundIsOOG === filters.isOOG),

    // isDamaged
    () =>
      filters.isDamaged === undefined ||
      (occupancy.isDamaged != null && !!occupancy.isDamaged === filters.isDamaged),
  ]

  return checks.every(check => check())
}

/**
 * Resolves the value used as the "bucket" for an occupancy under a colour-by
 * scheme. For the three string-keyed schemes (POD / Outbound Carrier /
 * Inbound Carrier) this is the journey's field value as a string. For the
 * Operator scheme, the journey carries a numeric `operatorId`, so we
 * stringify it; display names are resolved separately via CompanyStore.
 *
 * Returns `undefined` when the underlying field is null/undefined so callers
 * can treat "missing value" identically to "no scheme active" for that slot.
 */
export const getSchemeValue = (
  occupancy: OcuppancyWithFilterData,
  scheme: ColorScheme,
): string | undefined => {
  if (scheme === 'operator') {
    return occupancy.operatorId == null ? undefined : String(occupancy.operatorId)
  }
  const v = occupancy[scheme]
  return v == null ? undefined : v
}

/**
 * Resolves the background colour for a slot under a colour-by scheme.
 *
 * Resolution order, per occupant:
 *   1. Named selection wins — if any occupant's value is in the selected set,
 *      paint with that occupant's palette colour.
 *   2. If the planner has the Rest sentinel selected AND at least one occupant
 *      has a non-null value (i.e. there's a real bucket falling outside the
 *      named selection), paint Rest grey.
 *   3. Otherwise return `null` — caller falls back to the default occupancy
 *      gradient so unmatched slots remain visually unchanged.
 *
 * Multi-occupancy slots: the first occupant whose value is in the selected
 * set wins. Required because slot occupancies can include containers without
 * a value for the active scheme (e.g. POD=null) or with values the planner
 * has not selected; treating "first occupancy" as the slot's identity would
 * hide matching containers stacked behind them.
 */
/**
 * Single-occupancy variant of `resolveSlotSchemeColor`. Used by the detail
 * drawer and the per-container list, which paint each occupant individually
 * rather than picking one colour for a multi-occupancy slot.
 *
 * Returns the palette colour for the occupant's value if it's in the
 * selection, Rest grey if the Rest sentinel is selected and the occupant has
 * a non-null value, or `undefined` to leave the caller's default colouring
 * untouched.
 */
export const resolveOccupancySchemeColor = (
  occupancy: OcuppancyWithFilterData,
  scheme: ColorScheme | undefined,
  selectedValues: ReadonlySet<string>,
): string | undefined => {
  if (!scheme) return undefined
  const value = getSchemeValue(occupancy, scheme)
  if (value == null) return undefined
  if (selectedValues.has(value)) return paletteColorFor(scheme, value)
  if (selectedValues.has(OTHER_REST_VALUE)) return OTHER_REST_COLOR
  return undefined
}

export const resolveSlotSchemeColor = (
  occupancies: ReadonlyArray<OcuppancyWithFilterData>,
  scheme: ColorScheme | undefined,
  selectedValues: ReadonlySet<string>,
): string | null => {
  if (!scheme) return null
  let hasValueOutsideSelection = false
  for (const occ of occupancies) {
    const value = getSchemeValue(occ, scheme)
    if (value == null) continue
    if (selectedValues.has(value)) {
      return paletteColorFor(scheme, value)
    }
    hasValueOutsideSelection = true
  }
  if (selectedValues.has(OTHER_REST_VALUE) && hasValueOutsideSelection) {
    return OTHER_REST_COLOR
  }
  return null
}
